import React, { useRef, useEffect, useState } from 'react';
import { useTheme, makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';

import Popover from '@mui/material/Popover';
import StyledPaper from './../styled/paper';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';

import {
    scaleOrdinal as D3ScaleOrdinal,
    select as D3select,
} from 'd3';

import Hierarchy from '../../d3/D3Hierarchy';

const useStyles = makeStyles(theme => ({
    root: {
        '& .hierarchy .center': {
            fill: theme.palette.text.primary,
            fontSize: '1.5em',
            fontWeight: 200,
            cursor: 'default',
            userSelect: 'none',
        },

        '& .hierarchy .segment, .hierarchy .mini-segment': {
            stroke: theme.palette.text.primary,
            strokeWidth: 0.5,
        },

        '& .title.bkg': {
            stroke: `${theme.palette.background.default} !important`,
            fill: 'none',
            strokeWidth: 7,
            strokeLinejoin: 'round',
            strokeMiterlimit: 1,
            userSelect: 'none',
        },

        '& .pi.brush .selection': {
            stroke: theme.palette.secondary.main,
            // strokeWidth: 2,
        },

        '& .title, .subtitle': {
            textTransform: 'capitalize',
            fontSize: 12,
            fill: theme.palette.text.primary,
        },

        '& .brush .overlay': {
            fill: theme.styled.slider.overlay,
            fillOpacity: 0.3,
        },

        '& .brush .selection': {
            fillOpacity: 0.65,
            fill: theme.styled.slider.window,
        },

        '& .handle, .handle.pi': {
            fill: theme.palette.secondary.main,
            stroke: theme.palette.secondary.main,
        },

        '& .handle.pi': {
            fill: theme.palette.grey[300],
            fillOpacity: 0.55,
            stroke: theme.palette.grey[300],
        }
    }
}));

const AgentsHierarchy = (props) => {
    const d3Container = useRef(null);
    const theme = useTheme();
    const classes = useStyles();

    const [currentPopup, setCurrentPopup] = useState();

    const handleOnLink = () => {
        if (!currentPopup) return;
        props.onLink && props.onLink(currentPopup.d);
        setCurrentPopup();
    };

    const handleOnRemove = () => {
        if (!currentPopup) return;
        props.onRemove && props.onRemove(currentPopup.d);
        setCurrentPopup();
    };

    const handleOnClick = o => {
        setCurrentPopup({ d: o.d, elm: o.elt[0] });
    };

    const handleOnClose = () => {
        setCurrentPopup();
    };

    useEffect((didUpdate) => {
        if (d3Container.current) {
            const node = d3Container.current;
            const colors = D3ScaleOrdinal()
                .range(theme.ChartColors)
                .domain((props.data.children || []).map(d => d.name));

            const div = D3select(node).data(['div']).join('div');
            const g = div
                .selectAll('.user-agents')
                .data([props.data]);
            g.enter()
                .append('svg')
                .attr('class', 'user-agents')
                .style('display', 'block')
                .style('margin', 'auto')
                .merge(g)
                .attr('width', props.width)
                .attr('height', props.height)
                .call(Hierarchy()
                    .theme(theme)
                    .colors(colors)
                    .scale((props.scale || 'linear').toLowerCase())
                    .type((props.shape || 'circle').toLowerCase())
                    .width(props.width * 0.9)
                    .height(props.height * 0.9)
                    .animation((props.speed || 'fast').toLowerCase())
                    .on('click', handleOnClick));
            g.exit().remove();
        }
    }, [props.width, props.height, props.data, props.scale, props.shape, props.speed, theme]);

    return (
        <div>
            <div className={classes.root} name="hierarchy" ref={d3Container}></div>
            {currentPopup && <Popover
                open={Boolean(currentPopup)}
                anchorEl={currentPopup.elm}
                onClose={handleOnClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}>
                <StyledPaper className={classes.paper}>
                    <MenuItem onClick={handleOnLink}>
                        <ListItemIcon className={classes.itemIcon}>
                            <SearchIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="View Data" />
                    </MenuItem>
                    <MenuItem onClick={handleOnRemove}>
                        <ListItemIcon className={classes.itemIcon}>
                            <CancelIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Remove" />
                    </MenuItem>
                </StyledPaper>
            </Popover>}
        </div>
    );
};

AgentsHierarchy.propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    scale: PropTypes.string,
    shape: PropTypes.string,
    speed: PropTypes.string,
    data: PropTypes.object.isRequired,
    onLink: PropTypes.func,
    onRemove: PropTypes.func,
};

export default AgentsHierarchy;
